import { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  PREFERRED_LOCALE_KEY,
  SUPPORTED_LOCALES,
  basePath,
  cityName,
  getLocalizedLocation,
  localeFromPath,
  localizePath,
  translate,
  translateParts,
} from "@/i18n/locale";

const LanguageContext = createContext(null);

// Mount once inside the existing Router. Never key this provider or App by locale.
export function LanguageProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const locale = localeFromPath(location.pathname);

  useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = locale;
  }, [locale]);

  const setLanguage = useCallback((nextLocale) => {
    if (!SUPPORTED_LOCALES.includes(nextLocale)) return;
    // This is an explicit preference only. URL initialization never reads it or redirects.
    try {
      if (typeof window !== "undefined") window.localStorage.setItem(PREFERRED_LOCALE_KEY, nextLocale);
    } catch {
      // Browsers may disable storage; locale navigation still works.
    }
    const pathname = localizePath(location.pathname, nextLocale);
    if (pathname === location.pathname) return;
    navigate(
      { pathname, search: location.search, hash: location.hash },
      { state: location.state, preventScrollReset: true }
    );
  }, [location.pathname, location.search, location.hash, location.state, navigate]);

  const value = useMemo(() => ({
    locale,
    t: (text, values) => translate(text, locale, values),
    tParts: (text, values) => translateParts(text, locale, values),
    cityName: (raw) => cityName(raw, locale),
    getLocalizedLocation: (record) => getLocalizedLocation(record, locale),
    localizePath: (path, nextLocale = locale) => localizePath(path, nextLocale),
    basePath,
    setLanguage,
  }), [locale, setLanguage]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const value = useContext(LanguageContext);
  if (!value) throw new Error("useLanguage must be used within LanguageProvider");
  return value;
}
